import pako from 'pako';
import type { BootData } from '@/types/boot';
import { getBootPublicKey } from './getBootPublicKey';

function cleanPublicKey(publicKey: string): string {
  return publicKey
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s+/g, '');
}

function base64UrlToBase64(value: string): string {
  return value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function base64UrlToUint8Array(value: string): Uint8Array {
  return base64ToUint8Array(base64UrlToBase64(value));
}

function base64UrlToString(value: string): string {
  return new TextDecoder().decode(base64UrlToUint8Array(value));
}

async function getPublicKey(): Promise<CryptoKey> {
  // const publicKeyPem = getEnvVariable('VITE_BOOT_JWT_PUBLIC_KEY') || DEFAULT_BOOT_JWT_PUBLIC_KEY;
  const publicKeyPem = getBootPublicKey();
  const cleaned = cleanPublicKey(publicKeyPem);
  const spkiBytes = base64ToUint8Array(cleaned);
  const spkiDer = spkiBytes.buffer.slice(
    spkiBytes.byteOffset,
    spkiBytes.byteOffset + spkiBytes.byteLength
  ) as ArrayBuffer;

  return crypto.subtle.importKey('spki', spkiDer, { name: 'ECDSA', namedCurve: 'P-256' }, true, ['verify']);
}

function decodeCompressedPayload(d: string): Record<string, unknown> {
  const compressed = base64UrlToUint8Array(d);
  const decompressed = pako.inflate(compressed);
  const json = new TextDecoder().decode(decompressed);
  return JSON.parse(json) as Record<string, unknown>;
}

async function verifyAndDecodeJWT(jwt: string): Promise<Record<string, unknown>> {
  const [headerB64, payloadB64, signatureB64] = jwt.split('.');

  if (!headerB64 || !payloadB64 || !signatureB64) {
    throw new Error('Malformed JWT');
  }

  const header = JSON.parse(base64UrlToString(headerB64)) as { alg?: string };

  if (header.alg !== 'ES256') {
    throw new Error(`Unsupported alg: ${header.alg}`);
  }

  const publicKey = await getPublicKey();
  const dataToVerify = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
  const signature = base64UrlToUint8Array(signatureB64);

  const isValid = await crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    publicKey,
    signature as BufferSource,
    dataToVerify as BufferSource
  );

  if (!isValid) {
    throw new Error('Invalid signature');
  }

  const payload = JSON.parse(base64UrlToString(payloadB64)) as { d?: string };

  if (!payload.d) {
    throw new Error('Payload does not contain d');
  }

  return decodeCompressedPayload(payload.d);
}

/**
 * True when the URL hash carries a `boot` param — the precondition for
 * `getBoot()` to ever settle. When it's absent, `getBootFromUrlHash` returns a
 * deliberately never-settling promise (to keep the loading skeleton up), so
 * callers that must NOT hang on boot (e.g. Mixpanel SDK init, which only reads
 * a feature flag off boot) should branch on this instead of awaiting
 * `getBoot()`. When a boot param IS present, `getBoot()` always settles
 * (resolve, or reject on a bad signature/format).
 */
export const hasBootParam = (): boolean => {
  const hash = window.location.hash;
  if (!hash || hash.length <= 1) {
    return false;
  }
  const hashParams = new URLSearchParams(hash.substring(1));
  return Boolean(hashParams.get('boot'));
};

type BootHint = Pick<BootData, 'checkoutConfiguration' | 'featureFlags'>;

let bootHint: BootHint | null | undefined;

const readBootValueFromHash = (): string | null => {
  const hash = window.location.hash;
  if (!hash || hash.length <= 1) return null;
  return new URLSearchParams(hash.substring(1)).get('boot');
};

/**
 * Synchronous, UNVERIFIED peek at the boot payload — used ONLY to choose the
 * boot-skeleton shape (compact bottom sheet vs full-screen modal) on the very
 * first render, before the async ECDSA verify in {@link getBoot} lands. Without
 * it the `boot === null` first frame makes `CheckoutSkeletonLoader` fall back
 * to the DEFAULT full-screen two-pane modal for a beat, which reads as "the
 * checkout opened full screen first" before the sheet slides in.
 *
 * Skipping the signature check is safe here: this NEVER gates real checkout
 * behavior (the verified `getBoot()` still does) — it only picks a loading
 * placeholder. Cached; any parse failure falls back to `null` (→ the existing
 * DEFAULT skeleton, i.e. no behavior change).
 */
export const peekBootConfigSync = (): BootHint | null => {
  if (bootHint !== undefined) return bootHint;
  bootHint = null;
  try {
    const bootValue = readBootValueFromHash();
    if (!bootValue) return bootHint;

    // Only the signed-JWT shape is read here. Unsigned/dotless boots are no
    // longer accepted anywhere (ACDEV-30546), so a dotless value fails soft to
    // null (→ DEFAULT skeleton). NB: this sync peek still does NOT verify the
    // signature — it only picks a loading placeholder; the verified getBoot()
    // remains the sole gate for real checkout behavior.
    if (!bootValue.includes('.')) return bootHint;

    const payloadB64 = bootValue.split('.')[1];
    if (!payloadB64) return bootHint;
    const payload = JSON.parse(base64UrlToString(payloadB64)) as { d?: string };
    if (!payload.d) return bootHint;
    const data = decodeCompressedPayload(payload.d);

    bootHint = {
      checkoutConfiguration: data.checkoutConfiguration as BootData['checkoutConfiguration'],
      featureFlags: data.featureFlags as BootData['featureFlags'],
    };
  } catch {
    bootHint = null;
  }
  return bootHint;
};

/** Test-only: clears the memoized {@link peekBootConfigSync} result. */
export const __resetBootHintForTests = (): void => {
  bootHint = undefined;
};

export const getBootFromUrlHash = async (): Promise<BootData> => {
  const hash = window.location.hash;
  if (!hash || hash.length <= 1) {
    return new Promise<BootData>(() => {});
  }

  const hashContent = hash.substring(1);
  const hashParams = new URLSearchParams(hashContent);
  const bootValue = hashParams.get('boot');

  if (!bootValue) {
    return new Promise<BootData>(() => {});
  }

  // Signed ES256 JWT is the ONLY accepted boot format. The legacy unsigned
  // base64 branch was a signature-verification bypass (ACDEV-30546, CWE-347):
  // a dotless payload skipped verification entirely and any attacker-authored
  // boot was trusted. A dotless value now falls through to verifyAndDecodeJWT,
  // which rejects it as malformed.
  return (await verifyAndDecodeJWT(bootValue)) as unknown as BootData;
};

/**
 * Perf: module-scope cache for the boot decode promise. The boot JWT is in
 * the URL hash and decoded by both `useGetBoot` (via `CheckoutContent` AND
 * `CheckoutSkeletonLoader`) and `main.tsx`'s preflight. Without this cache,
 * pako.inflate + ECDSA verify would run multiple times in parallel on the
 * critical path, competing for the main thread at the most CPU-sensitive
 * moment. With the cache they all await the same single decode.
 */
let bootPromise: Promise<BootData> | null = null;

export const getBoot = (): Promise<BootData> => {
  if (!bootPromise) {
    bootPromise = getBootFromUrlHash();
  }
  return bootPromise;
};
